/**
 * Small shared UI for the Home: the app's icons (white sprites tinted through
 * CSS masks), type badges, and the dialogs (prompt / confirm / new project).
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { libraryUrl, templateUrl } from "../data/media";
import { t } from "../player/i18n";
import { AppAlert } from "./AppAlert";

/** One of the app's menu icons (`/libraries/ui/icon-<name>.png`), tinted with `currentColor`. */
export function Icon({ name, className, size }: { name: string; className?: string; size?: number }) {
  const url = libraryUrl(`ui/icon-${name}.png`);
  return (
    <span
      className={`icon ${className ?? ""}`}
      aria-hidden
      style={{ WebkitMaskImage: `url(${url})`, maskImage: `url(${url})`, ...(size ? { width: size, height: size } : undefined) }}
    />
  );
}

export const isQuizType = (type: string) => type === "quiz" || type === "quizDemo";

/** Round dark badge with the game / quiz icon (as on the app's project cards). */
/** Apple's sign-in button (black, the Apple logo and "Continue with Apple"), as Apple's guidelines ask. */
export function AppleButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className="btn btn-apple" onClick={onClick} disabled={disabled}>
      <svg viewBox="0 0 814 1000" width="16" height="19" aria-hidden="true">
        <path
          fill="currentColor"
          d="M788 341c-6 4-108 62-108 190 0 149 131 202 135 203-1 3-21 72-69 143-43 62-88 124-157 124s-87-40-166-40c-77 0-105 41-168 41s-107-57-158-128C38 790-10 659-10 535c0-199 130-305 257-305 68 0 124 44 167 44 41 0 105-47 182-47 30 0 134 3 192 114zM554 158c32-38 54-90 54-143 0-7-1-15-2-21-52 2-113 35-150 77-29 33-56 85-56 138 0 8 1 16 2 19 3 1 9 2 14 2 46 0 105-31 138-72z"
        />
      </svg>
      <span>{t("appleContinue")}</span>
    </button>
  );
}

export function TypeBadge({ type }: { type: string }) {
  return (
    <span className="type-badge" title={isQuizType(type) ? t("quizzes") : t("games")}>
      <Icon name={isQuizType(type) ? "quizzes" : "games"} />
    </span>
  );
}

/** Card art: the template's home image (or a flat colour for the colour "templates"). */
/** Templates offered for new projects and in the project settings (TemplateType). */
export const TEMPLATE_CHOICES = ["makeIt", "blocks", "blueSky", "buttermelt", "numbers", "vowels", "paper", "pastel", "polkaDot", "purpleLush", "rougeStar", "springLeaf", "wall", "carbon", "kilt", "comic", "waterColor", "concrete", "hexagon", "xmas", "circuit", "green", "whiteBoard", "notebook", "map", "oldPhoto", "stripes"];
export const COLOR_CHOICES = ["#b45255", "#fc5b60", "#fca465", "#fed46d", "#fefd73", "#ceec75", "#7fc277", "#5b9473", "#68c3bb", "#76fefe", "#5badef", "#8786d3", "#4d5f9f", "#925ca8", "#bb588d", "#fc63af", "#76665d"];

export function templateArt(template: string): { backgroundImage?: string; backgroundColor?: string } {
  if (template.startsWith("#")) return { backgroundColor: template };
  return { backgroundImage: `url(${templateUrl(template, "home")})` };
}

/**
 * The project page's banner (HomeProjectPanel): the template's wide panel
 * image stretched to it, as Unity does, so all of it shows (the 4:3 card art
 * cut to the banner kept only its blank middle). The default template has no
 * panel image and keeps its card art.
 */
export function templateBanner(template: string): { backgroundImage?: string; backgroundColor?: string; backgroundSize?: string } {
  if (!TEMPLATE_CHOICES.includes(template)) return templateArt(template);
  return { backgroundImage: `url(${templateUrl(template, "panel")})`, backgroundSize: "100% 100%" };
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

export function Modal({ title, onClose, children, className }: { title?: string; onClose: () => void; children: ReactNode; className?: string }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-veil" onClick={onClose}>
      <div className={`modal ${className ?? ""}`} role="dialog" aria-modal onClick={(e) => e.stopPropagation()}>
        {title && <h2>{title}</h2>}
        {children}
      </div>
    </div>
  );
}

export function PromptDialog({ title, label, initial = "", submitLabel, onSubmit, onClose }: { title: string; label: string; initial?: string; submitLabel?: string; onSubmit: (value: string) => Promise<void> | void; onClose: () => void }) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const v = value.trim();
    if (!v || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(v);
      onClose();
    } catch {
      setError(t("errGeneric"));
      setBusy(false);
    }
  };
  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit} className="dialog-form">
        <input dir="auto" ref={ref} className="text-input" value={value} onChange={(e) => setValue(e.target.value)} placeholder={label} aria-label={label} maxLength={100} />
        {error && <p className="error">{error}</p>}
        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t("cancel")}
          </button>
          <button type="submit" className="btn" disabled={!value.trim() || busy}>
            {busy ? "…" : (submitLabel ?? t("save"))}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * A yes/no question with the app's alert (AlertsController: ConfirmDeleteFolder,
 * DeleteProject, ConfirmDeleteClassrom, ConfirmDeleteStudent,
 * ConfirmLeaveClassroom…): title, message, Cancel on the left and the action on
 * the right. The alert waits for onConfirm and shows its failure.
 */
export function ConfirmDialog({ title, text, confirmLabel, onConfirm, onClose }: { title: string; text?: string; confirmLabel: string; /** kept for callers; the app's alert has one look */ danger?: boolean; onConfirm: () => Promise<void> | void; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch {
      setError(t("errGeneric"));
      setBusy(false);
    }
  };
  return <AppAlert title={title} message={text ?? ""} okLabel={confirmLabel} cancelLabel={t("cancel")} onOk={() => void go()} onCancel={onClose} busy={busy} error={error} />;
}

/** "…" menu: a button that opens a small list of actions. */
export function MoreMenu({ items, label }: { items: { label: string; icon?: string; danger?: boolean; onClick: () => void }[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div className="more" ref={ref}>
      <button className="more-btn" onClick={() => setOpen((o) => !o)} aria-label={label ?? "…"} aria-expanded={open}>
        <span />
        <span />
        <span />
      </button>
      {open && (
        <ul className="more-menu" role="menu">
          {items.map((it, i) => (
            <li key={i}>
              <button
                role="menuitem"
                className={it.danger ? "danger" : ""}
                onClick={() => {
                  setOpen(false);
                  it.onClick();
                }}
              >
                {it.icon && <Icon name={it.icon} />}
                {it.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Searching projects by name
// ---------------------------------------------------------------------------

const fold = (x: string) => x.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Every word of the query is somewhere in the name (ignoring case and accents). */
export function matchesSearch(name: string, query: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const n = fold(name);
  return words.every((w) => n.includes(w));
}

export function SearchBox({ value, onChange, autoFocus }: { value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  return (
    <label className="search-box">
      <Icon name="search" />
      <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={t("searchProjects")} aria-label={t("searchProjects")} dir="auto" autoFocus={autoFocus} />
    </label>
  );
}
